import type { OpenAPI } from "simplydo/interfaces";
import type {
  IdeaSubmissibilityStatus,
  IdeaWithSubmissibilityStatus,
} from "simplydo/interfaces/types/ideaTypes";

import { pluralise } from "./helpers";
import { shouldShowIdeaEdiSection, shouldShowIdeaSection } from "./ideas";

export type SubmissibilityDetail = NonNullable<
  IdeaSubmissibilityStatus["detail"]
>[string];

export type SubmissibilityIncorrectField = NonNullable<
  SubmissibilityDetail["incorrectFields"]
>[number];

export const SUBMISSIBILITY_COLORS = {
  complete: "#28a745",
  incomplete: "#007bff",
  error: "#dc3545",
  background: "#ccc",
} as const;

const errorMessages = {
  required: "Please enter a value",
  wordLimit: "You are currently exceeding the word limit",
};

const requiredMessages: Record<string, string> = {
  dropdown: "Please select a value",
  optionMultiple: "Please select an option",
  image: "Please select an image",
  video: "Please upload a video",
  file: "Please upload a file",
  multiimage: "Please upload an image",
  multifile: "Please upload a file",
  slider: "Please enter a value",
  creditsafereport: "Please select a value",
};

export const getSubmissibilitySectionKey = (
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (section.type === "impact") {
    return section.id || "impactStatement";
  }
  return section.id;
};

export const getSectionSubmissibility = (
  idea: IdeaWithSubmissibilityStatus | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  const sectionKey = getSubmissibilitySectionKey(section);
  if (!sectionKey) {
    return undefined;
  }
  return idea?.submissibilityStatus?.detail?.[sectionKey];
};

/**
 * Whether an incorrect field has content that needs fixing, as opposed to simply being empty.
 * Empty required fields only report `type: "required"` without a description; partially
 * completed values (e.g. table rows missing required columns) include a description.
 */
export const isIncorrectFieldStarted = (
  incorrectField: SubmissibilityIncorrectField | undefined,
) => {
  if (!incorrectField) {
    return false;
  }
  return incorrectField.type !== "required" || !!incorrectField.description;
};

const hasStartedIncorrectFields = (submissibility: SubmissibilityDetail) =>
  (submissibility.incorrectFields || []).some(isIncorrectFieldStarted);

export const getSubmissibilityProgress = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility?.visibleFields) {
    return 0;
  }
  // Fields with content that needs fixing are not counted as completed by the API, but the user
  // has worked on them so they still count towards progress. Only individual template fields
  // (with an id) are counted, as other sections (e.g. impact) already reflect partial progress.
  const startedIncorrectFields = (submissibility.incorrectFields || []).filter(
    (field: SubmissibilityIncorrectField) =>
      !!field.id && isIncorrectFieldStarted(field),
  ).length;
  return Math.min(
    1,
    (submissibility.completedFields + startedIncorrectFields) /
      submissibility.visibleFields,
  );
};

export const getSubmissibilityColor = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility) {
    return undefined;
  }
  // Empty fields are not treated as errors so the user isn't greeted by a wall of red
  if (hasStartedIncorrectFields(submissibility)) {
    return SUBMISSIBILITY_COLORS.error;
  }
  if (submissibility.completedFields === submissibility.visibleFields) {
    return SUBMISSIBILITY_COLORS.complete;
  }
  return SUBMISSIBILITY_COLORS.incomplete;
};

export const isSubmissibilitySectionComplete = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility) {
    return false;
  }
  return submissibility.completedFields === submissibility.visibleFields;
};

export const groupIncorrectFields = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  const incorrectFields = submissibility?.incorrectFields || [];
  return {
    required: incorrectFields.filter(
      (field: SubmissibilityIncorrectField) => field.type === "required",
    ),
    wordLimit: incorrectFields.filter(
      (field: SubmissibilityIncorrectField) => field.type === "wordLimit",
    ),
    custom: incorrectFields.filter(
      (field: SubmissibilityIncorrectField) => field.type === "custom",
    ),
    error: incorrectFields.filter(
      (field: SubmissibilityIncorrectField) => field.type === "error",
    ),
  };
};

export const getSubmissibilitySummaryText = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility || !(submissibility.visibleFields || 0)) {
    return "";
  }

  const { required, wordLimit, custom, error } =
    groupIncorrectFields(submissibility);

  const optionalFields =
    (submissibility.visibleFields || 0) - (submissibility.requiredFields || 0);
  const optionalFieldsCompleted =
    (submissibility.completedFields || 0) -
    (submissibility.completedRequiredFields || 0);
  const remainingOptionalFields = optionalFields - optionalFieldsCompleted;
  const totalRemaining =
    (submissibility.incorrectFields?.length || 0) + remainingOptionalFields;

  let summary = "";
  if (required.length > 0) {
    summary = `${required.length} required`;
  }
  if (remainingOptionalFields > 0) {
    summary = summary
      ? `${summary} (${remainingOptionalFields} optional)`
      : `${remainingOptionalFields} optional`;
  }
  if (summary) {
    return `${summary} ${pluralise(totalRemaining, "field", "fields", false)} remaining`;
  }
  if (wordLimit.length > 0 || custom.length > 0 || error.length > 0) {
    return "Issues need to be resolved";
  }
  return "All fields completed!";
};

export const getIncorrectFieldErrorMessage = (
  incorrectField: SubmissibilityIncorrectField | undefined,
  fieldType?: string,
) => {
  if (!incorrectField?.type) {
    return null;
  }
  if (incorrectField.type === "required") {
    return (fieldType && requiredMessages[fieldType]) || errorMessages.required;
  }
  if (incorrectField.type === "wordLimit") {
    return errorMessages.wordLimit;
  }
  return incorrectField.description || errorMessages.required;
};

export const getFieldWordCount = (
  idea: IdeaWithSubmissibilityStatus | undefined,
  fieldId: string,
) => {
  return (
    idea?.submissibilityStatus?.wordCounts?.find(
      (w: { field: string; count: number }) => w.field === fieldId,
    )?.count || 0
  );
};

type IdeaTemplateSection = NonNullable<
  OpenAPI.Schemas["IdeaTemplate"]["body"]
>[number];

export type SubmissionChecklistSection = {
  /** Key of the section in `submissibilityStatus.detail` */
  key: string;
  /** Key the section is registered under for scrolling */
  scrollKey: string;
  name: string;
  /** Fields that block submission (missing, over word limit, invalid etc.) */
  blockingFields: SubmissibilityIncorrectField[];
  optionalRemaining: number;
};

const getSectionScrollKey = (section: IdeaTemplateSection) =>
  section.type === "impact" ? "impactStatement" : section.id;

/*
  Optional fields that are over the word limit or otherwise invalid are listed as blocking,
  so they are excluded from the optional count to avoid listing them twice
*/
const getOptionalRemaining = (
  submissibility: SubmissibilityDetail,
  section: IdeaTemplateSection,
) => {
  const optionalFields =
    (submissibility.visibleFields || 0) - (submissibility.requiredFields || 0);
  const optionalCompleted =
    (submissibility.completedFields || 0) -
    (submissibility.completedRequiredFields || 0);
  const optionalIncorrect = (submissibility.incorrectFields || []).filter(
    (incorrectField: SubmissibilityIncorrectField) => {
      const templateField = section.fields?.find(
        (field) => field.id === incorrectField.id,
      );
      return !!templateField && !templateField.required;
    },
  ).length;
  return Math.max(
    0,
    Math.ceil(optionalFields - optionalCompleted - optionalIncorrect),
  );
};

/**
 * Builds the list of sections with outstanding work before an idea can be submitted.
 * `sections` should already be filtered to the sections visible to the user.
 */
export const getSubmissionChecklist = (
  idea: IdeaWithSubmissibilityStatus | undefined,
  sections: IdeaTemplateSection[],
): SubmissionChecklistSection[] => {
  const checklist: SubmissionChecklistSection[] = [];
  for (const section of sections) {
    if (section.type === "assessment") {
      continue;
    }
    const submissibility = getSectionSubmissibility(idea, section);
    if (!submissibility?.visibleFields) {
      continue;
    }
    const blockingFields = submissibility.incorrectFields || [];
    const optionalRemaining = getOptionalRemaining(submissibility, section);
    if (blockingFields.length === 0 && optionalRemaining === 0) {
      continue;
    }
    checklist.push({
      key: getSubmissibilitySectionKey(section) ?? section.id,
      scrollKey: getSectionScrollKey(section),
      name: section.name ?? "",
      blockingFields,
      optionalRemaining,
    });
  }
  return checklist;
};

type ChecklistUser = Parameters<typeof shouldShowIdeaEdiSection>[0];
type ChecklistIdea = IdeaWithSubmissibilityStatus & {
  ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
};

/** Builds the submission checklist for the sections of an idea the user can see */
export const getIdeaSubmissionChecklist = (
  user: ChecklistUser | null,
  idea: ChecklistIdea | undefined,
): SubmissionChecklistSection[] => {
  if (!idea) {
    return [];
  }
  const sections = (idea.ownerIdeaTemplate?.body ?? []).filter((section) => {
    if (
      !shouldShowIdeaSection(
        idea as Parameters<typeof shouldShowIdeaSection>[0],
        section,
      )
    ) {
      return false;
    }
    if (section.type === "edi") {
      return shouldShowIdeaEdiSection(user ?? undefined, idea, section);
    }
    return true;
  });
  return getSubmissionChecklist(idea, sections);
};

export const getChecklistOptionalTotal = (
  checklist: SubmissionChecklistSection[],
) => checklist.reduce((total, section) => total + section.optionalRemaining, 0);

/** Short explanation of why a field blocks submission, shown next to its title */
export const getChecklistIssueText = (
  field: SubmissibilityIncorrectField,
  t: (key: string) => string,
) => {
  // Errors (e.g. impact statement issues) carry the full message in their title
  if (field.type === "error") {
    return null;
  }
  if (field.type === "wordLimit") {
    return t("ideas.submissionChecklist.issue.wordLimit");
  }
  if (field.description) {
    return field.description;
  }
  if (field.type === "required") {
    return t("ideas.submissionChecklist.issue.required");
  }
  return t("ideas.submissionChecklist.issue.invalid");
};
